import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from '../styles/configuration.module.scss';
import ComputeConfig from './computeConfig';
import NetworkingConfig from './networkingConfig';
import StorageConfig from './storageConfig';
import CodeConfig from './codeConfig';
import DiskConfig from './diskConfig';
import ArtifactoryConfig from './artifactoryConfig';
import OpenshiftConfig from './openshiftConfig';
import { getServices, addServices, prices } from '../constants/configuration';
import ConfigurationsFooter from '../components/configurationsFooter';
import { AiOutlineFilePdf } from "@react-icons/all-files/ai/AiOutlineFilePdf";
import { displayTbOrGb } from '../helpers/sizesConverter';
import { jsPDF } from "jspdf";
import InfrastructureConfig from './infrastructureConfig';
import EquipmentConfig from './equipmentConfig';

function FirstStep({ changeStep, setConfig, intialConfigs, className, setPdf }) {
    const { t } = useTranslation('configuration');

    const services = getServices(t);
    
    const cores = {
        12: '1x12 Cores',
        24: '2x12 Cores',
        40: '2x20 Cores',
        56: '2x28 Cores',
        72: '4x18 Cores',
        96: '2x24 Cores',
        144: '4x36 Cores'
    };

    let initialPrice = 0;
    const [type, setType] = useState('public');
    const [owner, setOwner] = useState('icdcTeam');
    const [subscription, setSubscription] = useState('basic');
    const doc = new jsPDF();
    const [mainServicesConfigs, setMainServicesConfigs] = useState({
        infrastructure: intialConfigs.infrastructure,
        compute: intialConfigs.compute,
        networking: intialConfigs.networking,
        storage: intialConfigs.storage,
        code: intialConfigs.code,
        disk: intialConfigs.disk,
        artifactory: intialConfigs.artifactory,
        openshift: intialConfigs.openshift,
        equipment: intialConfigs.equipment,
    });

    const [addServicesConfig, setAddServicesConfig] = useState({
        compliance: intialConfigs.compliance,
        migrator: intialConfigs.migrator,
        devops: intialConfigs.devops,
        projects: intialConfigs.projects
    });

    const [price, setPrice] = useState(initialPrice);

    const { compute, networking, storage, code, disk, artifactory, openshift, equipment, infrastructure } = mainServicesConfigs;
    const { compliance, migrator, devops, projects } = addServicesConfig;

    const allStorage = infrastructure.storage.ssd.size * infrastructure.storage.ssd.nodes + infrastructure.storage.hdd.size * infrastructure.storage.hdd.nodes;

    const freeCpu = Math.floor(infrastructure.compute.computeNodes * infrastructure.compute.cpu * 0.85);
    const usedCpu = infrastructure.compute.computeNodes * infrastructure.compute.cpu - freeCpu;
    const freeRam = Math.floor(infrastructure.compute.computeNodes * infrastructure.compute.ram * 0.85);
    const usedRam = infrastructure.compute.computeNodes * infrastructure.compute.ram - freeRam;
    const freeStorage = Math.floor(allStorage * 0.85);
    const usedStorage = allStorage - freeStorage;

    const returnUsable = (disk) => infrastructure.storage[disk].size * infrastructure.storage[disk].nodes / 3;

    const ssdUsable = returnUsable('ssd');

    const hddUsable = returnUsable('hdd');

    const storageNodes = infrastructure.storage.isExternal ? 0 : infrastructure.storage.ssd.nodes + infrastructure.storage.hdd.nodes;

    const pdu = parseFloat(400 * (infrastructure.compute.computeNodes + (infrastructure.storage.ssd.nodes + infrastructure.storage.hdd.nodes)) / 0.9).toFixed(1);

    const kva = ((infrastructure.compute.computeNodes + infrastructure.storage.ssd.nodes + infrastructure.storage.hdd.nodes) + 4) * 2;

    const quantity = 9 + storageNodes * 2 + infrastructure.compute.computeNodes * (infrastructure.compute.gpu ? 2 : 1);

    doc.setFontSize(24);
    doc.text("Summary\n\n\n", 20, 20);
    doc.setFontSize(20);
    doc.text(`Cloud type: ${type === 'public' ? 'Public' : 'Private'}\nInstaller team: ${owner === 'icdcTeam' ? 'ICDC Team' : 'My Team'}\n\n`, 20, 30);
    doc.text(`Environment specification:\n`, 20, 48);

    doc.setFontSize(16);
    doc.text(`Rack space: ${quantity}\nPower Supply: ${kva + ' KVA'}\nPDU: ${pdu}\nPatch Cable: ${(infrastructure.compute.computeNodes + storageNodes) * 3}\nDirect-Attached Copper Cables: ${(infrastructure.compute.computeNodes + storageNodes) * 2}\nSFP transceiver: 2x\nFiber-optic cable: 2x`, 20, 54);

    doc.setFontSize(20);
    doc.text(`Hardware resources:`, 20, 104);
    doc.setFontSize(16);
    doc.text(`CPU: ${infrastructure.compute.cpu * infrastructure.compute.computeNodes + ' Cores'}\nRAM: ${displayTbOrGb(infrastructure.compute.computeNodes * infrastructure.compute.ram)}\nStorage: ${displayTbOrGb(allStorage)}\nGPU: ${infrastructure.compute.gpu ? `${infrastructure.compute.gpu * infrastructure.compute.computeNodes} GiB` : 'None'}`, 20, 111);

    doc.setFontSize(20);
    doc.text(`System infrastructure:`, 120, 104);
    doc.setFontSize(16);
    doc.text(`CPU: ${usedCpu} Cores\nRAM: ${displayTbOrGb(usedRam)}\nStorage: ${displayTbOrGb(usedStorage)}\nGPU: None`, 120, 111);

    doc.setFontSize(20);
    doc.text(`Available resources:`, 20, 141);
    doc.setFontSize(16);
    doc.text(`CPU: ${freeCpu} Cores\nRAM: ${displayTbOrGb(freeRam)}\nStorage: ${displayTbOrGb(freeStorage)}\nGPU: ${infrastructure.compute.gpu ? `${infrastructure.compute.gpu * infrastructure.compute.computeNodes} GiB` : 'None'}`, 20, 148);

    doc.setFontSize(20);
    doc.text(`Services:`, 20, 179);
    doc.setFontSize(16);
    doc.text(`Compute\nNetworking${storage ? '\nStorage' : ''}${code.isCheck ? '\nCode' : ''}${disk.isCheck ? '\nDisk' : ''}${artifactory.isCheck ? '\nArtifactory' : ''}${openshift.isCheck ? `\nOpenShift` : ''}${compliance ? '\nCompliance' : ''}`, 20, 185);

    doc.setFontSize(20);
    doc.text(`Support: ${subscription === 'basic' ? 'Basic' : 'Standard'}\nPrice: ${price}$`, 120, 179);

    useEffect(() => {
        initialPrice = infrastructure.compute.computeNodes * (prices[subscription].cpu * infrastructure.compute.cpu + prices[subscription].ram *
            infrastructure.compute.ram + prices[subscription].gpu * infrastructure.compute.gpu) + (infrastructure.storage.ssd.nodes * infrastructure.storage.ssd.size + infrastructure.storage.hdd.nodes * infrastructure.storage.hdd.size) * prices[subscription].storage;
        setPrice(parseFloat(initialPrice + (code.isCheck ? initialPrice * 0.1 : 0) + (disk.isCheck ? initialPrice * 0.2 : 0) + (artifactory.isCheck ? initialPrice * 0.1 : 0) + (openshift.isCheck ? initialPrice : 0) + (compliance ? initialPrice * 0.05 : 0)).toFixed(1));
    }, [mainServicesConfigs, addServicesConfig, subscription]);

    const changeMainConfig = (value, service) => setMainServicesConfigs(prevState => ({ ...prevState, [service]: value}));

    return <div className={className || styles.config}>
        <h2>{t('titleConfigure')}</h2>
        <p>{t('selectCloud')}</p>
        <div className={styles.first}>
            <div className={styles.column}>
                <div className={styles.types}>
                    <div className={(type === 'public' ? styles.checked : '') + ' flex'} onClick={() => setType('public')}>
                        <h3>{t('public')}</h3>
                        <p>{t('forProviders')}</p>
                    </div>
                    <div className={(type === 'private' ? styles.checked : '') + ' flex'} onClick={() => setType('private')}>
                        <h3>{t('private')}</h3>
                        <p>{t('forPersonal')}</p>
                    </div>
                </div>
                <p>{t('owner')}</p>
                <div className={styles.types}>
                    <div className={(owner === 'icdcTeam' ? styles.checked : '') + ' flex'} onClick={() => setOwner('icdcTeam')}>
                        <h3>{t('icdcTeam')}</h3>
                    </div>
                    <div className={(owner === 'otherTeam' ? styles.checked : '') + ' flex'} onClick={() => setOwner('otherTeam')}>
                        <h3>{t('otherTeam')}</h3>
                    </div>
                </div>
                <h4>{t('configuration')}</h4>
                <InfrastructureConfig service={services[10]}
                                intialConfigs={infrastructure}
                                changeConfigurations={newConf => changeMainConfig(newConf, 'infrastructure')}/>
                <EquipmentConfig service={services[1]}
                                intialConfigs={equipment}
                                changeConfigurations={newConf => changeMainConfig(newConf, 'equipment')} />
                <h4>{t('core')}</h4>
                <StorageConfig  service={services[3]}
                                intialConfigs={storage}
                                changeConfigurations={newConf => changeMainConfig(newConf, 'storage')}
                                space={{
                                    ssd: {
                                        free: 100 - (compute.ssd + openshift.space + openshift.blockSsd + openshift.filesystem + openshift.s3 + openshift.fs + disk.space + artifactory.ssd + code.ssd),
                                        total: ssdUsable
                                    },
                                    hdd: hddUsable ? {
                                        free: 100 - (compute.hdd + openshift.blockHdd),
                                        total: hddUsable
                                    } : null
                                }} />
                <ComputeConfig  service={services[0]}
                                intialConfigs={compute}
                                changeConfigurations={newConf => changeMainConfig(newConf, 'compute')}
                                space={{
                                    ssd: {
                                        free: 100 - (storage.ssd.s3 + storage.ssd.iscsi + openshift.space + openshift.blockSsd + openshift.filesystem + openshift.s3 + openshift.fs + disk.space + artifactory.ssd + code.ssd),
                                        total: ssdUsable
                                    },
                                    hdd: hddUsable ? {
                                        free: 100 - (storage.hdd.s3 + storage.hdd.iscsi + (openshift?.blockHdd || 0)),
                                        total: hddUsable
                                    } : null
                                }}
                                computeResources={{
                                    ram: freeRam,
                                    cpu: freeCpu
                                }} />
                <NetworkingConfig   service={services[2]}
                                    intialConfigs={networking}
                                    changeConfigurations={newConf => changeMainConfig(newConf, 'networking')} />
                <h4>{t('addServices')}</h4>
                <OpenshiftConfig service={services[7]}
                                intialConfigs={openshift}
                                changeConfigurations={newConf => changeMainConfig(newConf, 'openshift')}
                                total={10}
                                ssd={{
                                    free: 100 - (compute.ssd + storage.ssd.s3 + storage.ssd.iscsi + disk.space + artifactory.ssd + code.ssd),
                                    total: ((infrastructure.storage.isExternal ? 1 : infrastructure.storage.ssd.nodes) * infrastructure.storage.ssd.size) / (infrastructure.storage.isExternal ? 1 : 3)
                                }}
                                hdd={hddUsable ? {
                                    free: 100 - (compute.hdd + storage.hdd.s3 + storage.hdd.iscsi),
                                    total: ((infrastructure.storage.isExternal ? 1 : infrastructure.storage.hdd.nodes) * infrastructure.storage.hdd.size) / (infrastructure.storage.isExternal ? 1 : 3)
                                } : null}
                                compute={infrastructure.compute} />
                <DiskConfig service={services[5]}
                            intialConfigs={disk}
                            changeConfigurations={newConf => changeMainConfig(newConf, 'disk')}
                            storage={{ 
                                free: 100 - (compute.ssd + storage.ssd.s3 + storage.ssd.iscsi + openshift.space + openshift.blockSsd + openshift.filesystem + openshift.s3 + openshift.fs + artifactory.ssd + code.ssd),
                                total: ssdUsable
                            }} />
                <CodeConfig service={services[4]}
                            intialConfigs={code}
                            changeConfigurations={newConf => changeMainConfig(newConf, 'code')}
                            storage={{ 
                                free: 100 - (compute.ssd + storage.ssd.s3 + storage.ssd.iscsi + openshift.space + openshift.blockSsd + openshift.filesystem + openshift.s3 + openshift.fs + disk.space + artifactory.ssd),
                                total: ssdUsable
                            }} />
                <ArtifactoryConfig  service={services[6]}
                                    intialConfigs={artifactory}
                                    changeConfigurations={newConf => changeMainConfig(newConf, 'artifactory')}
                                    storage={{ 
                                        free: 100 - (compute.ssd + storage.ssd.s3 + storage.ssd.iscsi + openshift.space + openshift.blockSsd + openshift.filesystem + openshift.s3 + openshift.fs + disk.space + code.ssd),
                                        total: ssdUsable
                                    }} />
                { addServices.map(addService => <div key={addService.name} className={styles['add-service']}>
                    <div
                        className={'flex pointer '}
                        onClick={() => setAddServicesConfig(prevState => ({ ...prevState, [addService.name.toLocaleLowerCase()]: !prevState[addService.name.toLocaleLowerCase()] }))}
                        >
                        <input className='pointer ' readOnly checked={addServicesConfig[addService.name.toLocaleLowerCase()]} type="checkbox" id={addService.name.toLocaleLowerCase()} />
                        <label htmlFor={addService.name.toLocaleLowerCase()} className={styles["sr-only"]}>{addService.name}</label>
                        <img src={addService.img} alt={t("addServiceAlt")} />
                        <h3>{addService.name}</h3>
                    </div>
                    <br />
                    <p className={styles.description}>{t(addService.description)}</p>
                </div> ) }
                <div className='flex' style={{ alignItems: 'center' }}>
                    <h4 style={{ marginRight: '1rem' }}>{t('supportSubscription')}</h4>
                    <a href='https://icdc.io/pricing' target='_blank' style={{ color: '#6549FF', fontSize: '12px', marginTop: '2rem' }}>
                        {t('learnMore')}
                        <span className='sr-only'>{t('learn_more_about_pricing')}</span>
                    </a>
                </div>
                <div className={styles.types}>
                    <div className={(subscription === 'basic' ? styles.checked : '') + ' flex col'} style={{ alignItems: 'flex-start' }} onClick={() => setSubscription('basic')}>
                        <p style={{ fontSize: '14px', fontWeight: '600' }}>{t('basic')}</p>
                        <p style={{ fontSize: '12px' }}>{t('basicSubsrc')}</p>
                    </div>
                    <div className={(subscription === 'standart' ? styles.checked : '') + ' flex col'} style={{ alignItems: 'flex-start' }} onClick={() => setSubscription('standart')}>
                        <p style={{ fontSize: '14px', fontWeight: '600' }}>{t('standart')}</p>
                        <p style={{ fontSize: '12px' }}>{t('standartSubscr')}</p>
                    </div>
                </div>
            </div>
            <div className={styles.column}>
                <div className={styles.summary} id='summary'>
                    <h2>{t('summary')}</h2>
                    <label><h3>{t('cloudType')}</h3><p>{t(type)}</p></label>
                    <label><h3>{t('installer')}</h3><p>{t(owner)}</p></label>
                    <h3>{t('specification')}</h3>
                    <div className='flex'>
                        <div className={styles['summary-service']}>
                            <div className='flex'>
                                <p className={styles.param}>{t('rack')}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{quantity}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`power.title`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{kva + ' KVA'}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`power.pdu`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{pdu}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`networking.patchCable`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{(infrastructure.compute.computeNodes + storageNodes) * 3}</p>
                            </div>
                        </div>
                        <div className={styles['summary-service']}>
                            <div className='flex'>
                                <p className={styles.param}>{t(`networking.cables`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{(infrastructure.compute.computeNodes + storageNodes) * 2}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t('transciver')} &nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;2x</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t('fiber')}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;2x</p>
                            </div>
                        </div>
                        <h4 style={{ width: '100%', 'marginBottom': '1rem' }}>{t('capacity')}</h4>
                        <div className={styles['summary-service']}>
                            <b>{t('hardware')}</b>
                            <div className='flex'>
                                <p className={styles.param}>CPU&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{infrastructure.compute.cpu * infrastructure.compute.computeNodes} Cores</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>RAM&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{displayTbOrGb(infrastructure.compute.computeNodes * infrastructure.compute.ram, t)}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>Storage&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{displayTbOrGb(allStorage, t)}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`compute.gpu`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{infrastructure.compute.gpu ? `${infrastructure.compute.gpu * infrastructure.compute.computeNodes} ${t("gib")}` : 'None'}</p>
                            </div>
                        </div>
                        <div className={styles['summary-service']}>
                            <b>{t('system')}</b>
                            <div className='flex'>
                                <p className={styles.param}>CPU&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{usedCpu} Cores</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>RAM&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{displayTbOrGb(usedRam, t)}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>Storage&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{usedStorage} {t("gib")}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`compute.gpu`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{'None'}</p>
                            </div>
                        </div>
                        <div className={styles['summary-service']}>
                            <b>{t('available')}</b>
                            <div className='flex'>
                                <p className={styles.param}>CPU&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{freeCpu} Cores</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>RAM&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{displayTbOrGb(freeRam, t)}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>Storage&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{freeStorage} {t("gib")}</p>
                            </div>
                            <div className='flex'>
                                <p className={styles.param}>{t(`compute.gpu`)}&nbsp;</p>
                                <p className={styles['bottom-dots']}></p>
                                <p className={styles.value}>&nbsp;{infrastructure.compute.gpu ? `${infrastructure.compute.gpu * infrastructure.compute.computeNodes} ${t("gib")}` : 'None'}</p>
                            </div>
                        </div>
                    </div>
                    <label>
                        <p dangerouslySetInnerHTML={{__html: `<span class=${styles.bold}>${t('services')}</span>
                            <nobr> Compute,</nobr>
                            Networking${storage ? `, Storage` : ''}${code.isCheck ? ', Code' : ''}${disk.isCheck ? ', Disk' : ''}${artifactory.isCheck ? ', Artifactory' : ''}${openshift.isCheck ? `, OpenShift` : ''}${compliance ? ', Compliance' : ''}`
                        }} />
                    </label>
                    <label>
                        <h4>{t('support')}:</h4>
                        <p>{t(subscription)}</p>
                    </label>
                </div>
                <div className={'flex col ' + styles.costs}>
                        <div className='flex'>
                            <p>{t('upfrontCost')}</p>
                            <p className={styles.price}>{ owner === 'icdcTeam' ? t('onDemand') : '$0.00'}</p>
                        </div>
                        <div className='flex'>
                            <p>{t('monthlyCost')}</p>
                            <p className={styles.price}>${price}</p>
                        </div>
                </div>
                <div className={styles.export}>
                    <button onClick={() => { setPdf(doc); setConfig({ ...mainServicesConfigs, ...addServicesConfig, price, subscription, type }); changeStep(1); }}>
                        <AiOutlineFilePdf style={{ fontSize: '20px' }} />
                        Export
                    </button>
                </div>
                <div className={'flex ' + styles['contact-info']}>
                    <p style={{ width: '100%' }}>
                        {t('responsibility')}
                    </p>
                </div>
                <div className={'flex ' + styles['contact-info']}>
                    <p>{t('pricesDescription')}</p>
                    <button onClick={() => {
                        changeStep(1);
                        setConfig({ ...mainServicesConfigs, ...addServicesConfig, price, subscription, type });
                    }} className='btn cloud'>{t('contactInfo')}</button>
                </div>
            </div>
        </div>
        <ConfigurationsFooter />
    </div>
};

export default memo(FirstStep);
